import { APPLY_DECISION_LABELS, resolveJobStatusLabel } from '../../state/jobOpportunities'
import type { JobOpportunity } from '../../state/jobOpportunities'

export type JobActivityKind = 'status' | 'decision'

export interface JobActivityEntry {
  kind: JobActivityKind
  // The stored value: a status id, a decision value, or 'reopened'.
  key: string
  label: string
  // ISO timestamp as stored. May be absent or unparseable on old records.
  at: string
  by?: string
}

// update-job-opportunity appends this to decisionHistory when staff restore an
// archived job for a fresh decision. It is not an APPLY_DECISIONS value.
const REOPENED = 'reopened'

export function decisionActivityLabel(key: string): string {
  if (key === REOPENED) return 'Reopened'
  return APPLY_DECISION_LABELS[key] || key
}

/**
 * One timeline from the two arrays the server keeps on a job, newest first.
 *
 * Answering a decision on a job with no status writes the decision and the
 * `applied` status with the same timestamp, so ties are normal. The status is
 * the consequence, so it ranks as the newer of the two. Entries whose
 * timestamp cannot be parsed sort last, the later-stored one first.
 */
export function buildJobActivity(
  job: Pick<JobOpportunity, 'statusHistory' | 'decisionHistory'>
): JobActivityEntry[] {
  const statuses = Array.isArray(job.statusHistory) ? job.statusHistory : []
  const decisions = Array.isArray(job.decisionHistory) ? job.decisionHistory : []

  const rows = [
    ...decisions.map((d, seq) => ({
      seq,
      rank: 0,
      entry: {
        kind: 'decision' as const,
        key: d.decision,
        label: decisionActivityLabel(d.decision),
        at: d.at,
        by: d.changedBy?.split('@')[0],
      },
    })),
    ...statuses.map((s, seq) => ({
      seq,
      rank: 1,
      entry: {
        kind: 'status' as const,
        key: s.status,
        label: resolveJobStatusLabel(s.status),
        at: s.timestamp,
        by: s.changedBy?.split('@')[0],
      },
    })),
  ]

  const time = (at: string) => {
    const ms = Date.parse(at)
    return Number.isNaN(ms) ? -Infinity : ms
  }

  return rows
    .sort((a, b) => {
      const byTime = time(b.entry.at) - time(a.entry.at)
      // -Infinity - -Infinity is NaN, so unparseable pairs fall through to rank.
      if (byTime !== 0 && !Number.isNaN(byTime)) return byTime
      if (a.rank !== b.rank) return b.rank - a.rank
      return b.seq - a.seq
    })
    .map((r) => r.entry)
}
